/**
 * Aggiornamenti dal vivo dei gruppi WhatsApp: il core manda un broadcast 'group_update'
 * sul canale pubblico groups-{organizationId} (flusia-core inbound/handlers/group.ts).
 *
 * supabase.channel(nome) restituisce lo STESSO canale se esiste gia': una sola
 * iscrizione per organizzazione, condivisa da tutti i componenti (conteggio dei
 * riferimenti), altrimenti chi smonta per primo spegnerebbe il canale agli altri.
 */
import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { getGroupNamesCache } from '@/lib/stores/whatsapp/caches';
import { parseGroupUpdate, type GroupUpdate } from './groupLogic';

type Listener = (u: GroupUpdate) => void;

interface Shared {
  channel: ReturnType<typeof supabase.channel>;
  listeners: Set<Listener>;
}

const shared = new Map<string, Shared>();

/** Nome del gruppo aggiornato nella lista chat, nella chat aperta e nella cache dei nomi. */
function applySubjectToStore(u: GroupUpdate) {
  if (!u.subject) return;
  const state = useWhatsAppChatStore.getState();
  if (u.configId && state.wahaConfig?.configId && u.configId !== state.wahaConfig.configId) return;
  const subject = u.subject;
  getGroupNamesCache().set(u.groupId, subject);
  const inList = state.chats.some((c) => c.id === u.groupId && c.name !== subject);
  const selected = state.selectedChatData?.id === u.groupId && state.selectedChatData.name !== subject;
  if (!inList && !selected) return;
  useWhatsAppChatStore.setState((s) => ({
    chats: s.chats.map((c) => (c.id === u.groupId ? { ...c, name: subject } : c)),
    selectedChatData: s.selectedChatData?.id === u.groupId ? { ...s.selectedChatData, name: subject } : s.selectedChatData,
  }));
}

function subscribe(organizationId: string, listener: Listener): () => void {
  let entry = shared.get(organizationId);
  if (!entry) {
    const listeners = new Set<Listener>();
    const channel = supabase
      .channel(`groups-${organizationId}`)
      .on('broadcast', { event: 'group_update' }, ({ payload }) => {
        const u = parseGroupUpdate(payload);
        if (!u) return;
        applySubjectToStore(u);
        listeners.forEach((l) => { try { l(u); } catch (err) { console.error('[groups] listener', err); } });
      })
      .subscribe();
    entry = { channel, listeners };
    shared.set(organizationId, entry);
  }
  entry.listeners.add(listener);
  return () => {
    const e = shared.get(organizationId);
    if (!e) return;
    e.listeners.delete(listener);
    if (e.listeners.size === 0) {
      shared.delete(organizationId);
      supabase.removeChannel(e.channel);
    }
  };
}

/**
 * Ascolta gli aggiornamenti dei gruppi dell'organizzazione. Il nome nella lista chat
 * si aggiorna sempre; onUpdate (facoltativo) riceve ogni evento, es. per il pannello
 * del gruppo aperto.
 */
export function useGroupRealtime(organizationId: string | null | undefined, onUpdate?: Listener, enabled = true) {
  const cb = useRef(onUpdate);
  cb.current = onUpdate;
  useEffect(() => {
    if (!organizationId || !enabled) return;
    return subscribe(organizationId, (u) => cb.current?.(u));
  }, [organizationId, enabled]);
}
